import React, { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGoogleLogin } from "@react-oauth/google";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useZustandStore from "../../../zustandStore/useZustandStore";
import AgencyAPIs from "../../../apiRoutes/Agency";
import AdsAPIs from "../../../apiRoutes/Ads";
import LoginAPIs from "../../../apiRoutes/Login";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../../utils/agencyScope";
import { clipboardGrid } from "../../../utils/clipboard";
import {
  DIRECT_DELIVERY_KEYS,
  DIRECT_OPTIONAL_KEYS,
  HOOK_LINK_KEYS,
  directColumns,
  isUnusedHookCell,
  visibleColumns,
  directDeliveryFields,
  directErrorMessage,
  directRowProblem,
  handleGridAdsetName,
} from "../utils/directRows";
import { GET_AGENCIES_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY, GET_AD_DELIVERIES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import CustomModal from "../../common/CustomModal";
import SearchableSelect from "../../common/SearchableSelect";
import MultiSelectCell from "../../common/MultiSelectCell";
import TwoLevelCell from "../../common/TwoLevelCell";
import useCtmOptionsByApp from "../../../hooks/useCtmOptionsByApp";
import { composeModule } from "../../../utils/adNameBuilder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import useAmsConfig from "../../../hooks/useAmsConfig";
import { AUTO_LAUNCH_OPTIONS } from "../../../utils/constants/AdRequest";
import { handleParseAutoLaunch } from "../../../utils/helperFunctions";
import { STICKY_ADSET_CELL, STICKY_ADSET_HEAD, columnWidth } from "../utils/gridLayout";
import toast from "react-hot-toast";

const PRIORITY_OPTIONS = ["low", "medium", "high"];

const GOOGLE_SHEETS_SCOPES =
  "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.file";

const isGoogleTokenRequired = (err) =>
  err?.response?.data?.error_code === "GOOGLE_TOKEN_REQUIRED";

// Columns shown in the review table (mirrors AD_REQUEST_SHEET_KEYS on backend)
const SHEET_KEYS = [
  "for_app_name", "language", "account", "category", "script_writer_name", "track",
  "track_number", "topic", "script_name", "module_type", "module", "creative_format",
  "visual_format", "human_ai", "script_type", "hooks_count", "priority",
  "due_delivery_date", "script", "comments", "reference_link", "auto_launch",
];
const SHEET_LABELS = [
  "App *", "Language *", "Account *", "Category *", "Script Writer", "Track *",
  "Track #", "Topic *", "Script Name *", "Module Type *", "Module *", "Creative Format *",
  "Visual Format *", "Human/AI *", "Script Type *", "Hooks", "Priority",
  "Due Date (DD-MM-YYYY) *", "Script *", "Comments", "Reference Link", "Auto launch",
];
// Backend rejects a row missing any of these; check up front so one bad row is named.
const REQUIRED_KEYS = [
  "for_app_name", "language", "account", "category", "track", "topic", "script_name",
  "module_type", "module", "creative_format", "visual_format", "human_ai", "script_type",
  "due_delivery_date", "script",
];

// How many hooks (= ads) one request can ask for; blank means 1.
const HOOKS_COUNT_MIN = 1;
const HOOKS_COUNT_MAX = 10;
const HOOKS_COUNT_OPTIONS = Array.from(
  { length: HOOKS_COUNT_MAX - HOOKS_COUNT_MIN + 1 },
  (_, i) => String(HOOKS_COUNT_MIN + i)
);

// Direct sheet (mirrors DIRECT_SHEET_KEYS on backend): no hooks/priority/due date, delivery columns added.
const DIRECT = directColumns(SHEET_KEYS, SHEET_LABELS);
const DIRECT_REQUIRED_KEYS = REQUIRED_KEYS.filter((k) => DIRECT.keys.includes(k) && !DIRECT_OPTIONAL_KEYS.has(k));
const HOOK_LINK_KEY_SET = new Set(HOOK_LINK_KEYS);

function createEmptyRow() {
  return Object.fromEntries([...SHEET_KEYS, ...DIRECT_DELIVERY_KEYS].map((k) => [k, ""]));
}

/** Convert DD-MM-YYYY → YYYY-MM-DD, returns null for invalid/blank. */
function parseDueDate(val) {
  if (!val || !val.trim()) return undefined;
  const parts = val.trim().split("-");
  if (parts.length !== 3) return undefined;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy || yyyy.length !== 4) return undefined;
  return `${yyyy}-${mm}-${dd}`;
}

// step: "agency" → "sheets" → "table"
const AdRequestSheetModal = ({ open, onClose, direct = false }) => {
  const cfg = useAmsConfig();
  const [rows, setRows] = useState([]);
  // Direct grid shows hook link columns up to the largest Hooks value picked.
  const shown = direct ? visibleColumns(DIRECT.keys, DIRECT.labels, rows) : null;
  const keys = direct ? shown.keys : SHEET_KEYS;
  // Hidden hook columns are always the trailing ones, so import and paste follow the full layout.
  const allKeys = direct ? DIRECT.keys : SHEET_KEYS;
  const labels = direct ? shown.labels : SHEET_LABELS;
  const requiredKeys = direct ? DIRECT_REQUIRED_KEYS : REQUIRED_KEYS;
  const title = direct ? "Deliver without request (Google Sheets)" : "Import Ad Requests from Google Sheets";
  const [agencyId, setAgencyId] = useState("");
  const [step, setStep] = useState(null);
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sheetId, setSheetId] = useState(null);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);
  const [isImportingSheet, setIsImportingSheet] = useState(false);
  const tableRef = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const agencyIdFromUser = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const effectiveAgencyId = agencyIdFromUser || (agencyId ? Number(agencyId) : null);
  const { getCategories, getTracks, getTrackNumber } = useCtmOptionsByApp({ enabled: open });

  useEffect(() => {
    if (open && step === null) {
      setStep(agencyScoped ? "sheets" : "agency");
    }
  }, [open, step, agencyScoped]);

  const { data: agenciesResult, isLoading: agenciesLoading } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, status: "approved", limit: 200, offset: 0 }),
    enabled: open && !agencyScoped,
  });
  const agencies = (agenciesResult?.data ?? []).filter((a) => !direct || a.allow_direct_delivery);
  const agencyName =
    agencies.find((a) => Number(a.id) === Number(effectiveAgencyId))?.name || currentUser?.agency_name || "";

  const { data: adTypesResult } = useQuery({
    queryKey: ["adtypes-for-agency", effectiveAgencyId],
    queryFn: () => AdsAPIs.listAdTypesForAgency({ axiosPrivate, agency_id: effectiveAgencyId }),
    enabled: open && direct && !!effectiveAgencyId,
  });
  const adTypeNames = (adTypesResult?.data ?? []).map((t) => t.name);

  const handleNext = () => {
    if (!effectiveAgencyId) return toast.error("Select an agency.");
    setStep("sheets");
  };

  const handleClose = () => {
    setAgencyId("");
    setStep(null);
    setRows([]);
    setSelectedRowIndices([]);
    setSheetId(null);
    onClose();
  };

  // ── Google auth ────────────────────────────────────────────────────────────

  const retryCallbackRef = useRef(null);

  const onAuthCodeReceived = useCallback(
    async (code) => {
      try {
        await LoginAPIs.saveGoogleSheetsToken({ axiosPrivate, code });
      } catch {
        toast.error("Failed to save Google authorization.");
        setIsCreatingSheet(false);
        setIsImportingSheet(false);
        return;
      }
      const retry = retryCallbackRef.current;
      retryCallbackRef.current = null;
      if (retry) retry();
    },
    [axiosPrivate]
  );

  const requestGoogleAuth = useGoogleLogin({
    flow: "auth-code",
    scope: GOOGLE_SHEETS_SCOPES,
    prompt: "consent",
    access_type: "offline",
    onSuccess: (codeResponse) => onAuthCodeReceived(codeResponse.code),
    onError: () => {
      toast.error("Google authorization failed. Please try again.");
      retryCallbackRef.current = null;
      setIsCreatingSheet(false);
      setIsImportingSheet(false);
    },
  });

  // ── Sheet actions ──────────────────────────────────────────────────────────

  const createSheet = async () => {
    setIsCreatingSheet(true);
    try {
      const result = await AdsAPIs.createAdRequestSheet({ axiosPrivate, direct, agency_id: effectiveAgencyId });
      if (result?.sheet_id && result?.sheet_url) {
        setSheetId(result.sheet_id);
        window.open(result.sheet_url, "_blank");
        toast.success("Google Sheet created and opened in a new tab.");
      } else {
        toast.error("Failed to create Google Sheet.");
      }
      setIsCreatingSheet(false);
    } catch (err) {
      if (isGoogleTokenRequired(err)) {
        retryCallbackRef.current = createSheet;
        requestGoogleAuth();
      } else {
        toast.error(err?.response?.data?.error_message || err?.message || "Failed to create sheet.");
        setIsCreatingSheet(false);
      }
    }
  };

  const importFromSheet = async () => {
    if (!sheetId) return toast.error("Create a Google Sheet first.");
    setIsImportingSheet(true);
    try {
      const importedRows = await AdsAPIs.importAdRequestSheet({ axiosPrivate, sheet_id: sheetId, direct, agency_id: effectiveAgencyId });
      if (!Array.isArray(importedRows) || importedRows.length === 0) {
        toast.error("No data found in the sheet.");
        setIsImportingSheet(false);
        return;
      }
      const mapped = importedRows.map((r) => {
        const row = Object.fromEntries(allKeys.map((k) => [k, r[k] ?? ""]));
        const num = getTrackNumber(row.for_app_name, row.language, row.category, row.track);
        return num ? { ...row, track_number: num } : row;
      });
      setRows(mapped);
      setSelectedRowIndices(mapped.map((_, i) => i));
      toast.success(`Imported ${mapped.length} row(s) from Google Sheet.`);
      setIsImportingSheet(false);
      setStep("table");
    } catch (err) {
      if (isGoogleTokenRequired(err)) {
        retryCallbackRef.current = importFromSheet;
        requestGoogleAuth();
      } else {
        toast.error(err?.response?.data?.error_message || err?.message || "Failed to import sheet.");
        setIsImportingSheet(false);
      }
    }
  };

  // ── Table helpers ──────────────────────────────────────────────────────────

  const toggleRow = (i) =>
    setSelectedRowIndices((prev) =>
      prev.includes(i) ? prev.filter((j) => j !== i) : [...prev, i].sort((a, b) => a - b)
    );

  const toggleSelectAll = () => {
    if (selectedRowIndices.length === rows.length) setSelectedRowIndices([]);
    else setSelectedRowIndices(rows.map((_, i) => i));
  };

  const updateCell = useCallback((rowIndex, key, value) => {
    setRows((prev) =>
      prev.map((r, idx) => {
        if (idx !== rowIndex) return r;
        const newRow = { ...r, [key]: value };
        if (key === "for_app_name" || key === "language") {
          // App and language drive the CTM cascade; reset downstream selections.
          if (key === "for_app_name") newRow.language = "";
          newRow.category = "";
          newRow.track = "";
          newRow.track_number = "";
        } else if (key === "category") {
          newRow.track = "";
          newRow.track_number = "";
        } else if (key === "track") {
          newRow.track_number = getTrackNumber(newRow.for_app_name, newRow.language, newRow.category, value);
        } else if (key === "module_type") {
          newRow.module = "";
        }
        return newRow;
      })
    );
  }, [getTrackNumber]);

  // Auto-match: rows whose (app, language, category, track) match a CTM mapping take its track_number,
  // replacing what the sheet or paste carried. Unmatched rows keep their own value.
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        const current = (r.track_number ?? "").toString().trim();
        if (!r.for_app_name || !r.language || !r.category || !r.track) return r;
        const num = getTrackNumber(r.for_app_name, r.language, r.category, r.track);
        if (!num || String(num) === current) return r;
        changed = true;
        return { ...r, track_number: num };
      });
      return changed ? next : prev;
    });
  }, [getTrackNumber, rows.length]);

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setSelectedRowIndices((prev) => [...prev, rows.length]);
  };

  const handleTablePaste = useCallback((e) => {
    const pastedGrid = clipboardGrid(e.clipboardData);
    if (!pastedGrid) return;
    const td = e.target.closest("td");
    if (!td) return;
    const tr = td.closest("tr");
    if (!tr) return;
    const tbody = tr.closest("tbody");
    if (!tbody) return;
    const allRows = Array.from(tbody.querySelectorAll("tr"));
    const startRowIdx = allRows.indexOf(tr);
    const tds = Array.from(tr.querySelectorAll("td"));
    const startColIdx = tds.indexOf(td) - 1;
    e.preventDefault();
    setRows((prev) => {
      const next = [...prev];
      while (next.length < startRowIdx + pastedGrid.length) next.push(createEmptyRow());
      for (let r = 0; r < pastedGrid.length; r++) {
        for (let c = 0; c < pastedGrid[r].length; c++) {
          const ri = startRowIdx + r;
          const ci = startColIdx + c;
          if (ci >= 0 && ci < allKeys.length) {
            next[ri] = { ...next[ri], [allKeys[ci]]: pastedGrid[r][c] };
          }
        }
      }
      return next;
    });
    setSelectedRowIndices((prev) => {
      const s = new Set(prev);
      for (let r = 0; r < pastedGrid.length; r++) s.add(startRowIdx + r);
      return Array.from(s).sort((a, b) => a - b);
    });
  }, [allKeys]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!effectiveAgencyId) return toast.error("Select an agency.");
    const selectedRows = rows.filter((_, i) => selectedRowIndices.includes(i));
    const nonEmpty = selectedRows.filter((r) =>
      allKeys.some((k) => (r[k] ?? "").toString().trim() !== "")
    );
    if (!nonEmpty.length) return toast.error("No rows with data to submit.");
    const rowNo = (r) => rows.indexOf(r) + 1;
    for (const r of nonEmpty) {
      const gap = requiredKeys.find((k) => !(r[k] ?? "").toString().trim());
      if (gap) return toast.error(`Row ${rowNo(r)}: ${labels[keys.indexOf(gap)].replace(" *", "")} is required.`);
      const problem = direct ? directRowProblem(r) : null;
      if (problem) return toast.error(`Row ${rowNo(r)}: ${problem}`);
    }
    const badHooks = nonEmpty.find((r) => {
      const raw = (r.hooks_count ?? "").toString().trim();
      if (raw === "") return false;
      const n = Number(raw);
      return !Number.isInteger(n) || n < HOOKS_COUNT_MIN || n > HOOKS_COUNT_MAX;
    });
    if (badHooks)
      return toast.error(
        `Hooks must be a whole number between ${HOOKS_COUNT_MIN} and ${HOOKS_COUNT_MAX} (blank = 1).`
      );

    const payloads = nonEmpty.map((row) => {
      const dueDate = parseDueDate(row.due_delivery_date);
      return {
        requested_for_agency_id: effectiveAgencyId,
        requested_via: "bulk_entry",
        for_app_name: row.for_app_name || undefined,
        language: row.language || undefined,
        account: row.account || undefined,
        category: row.category || undefined,
        script_writer_name: row.script_writer_name || undefined,
        track: row.track || undefined,
        track_number: (row.track_number ?? "").toString().trim() !== "" ? Number(row.track_number) : undefined,
        topic: row.topic || undefined,
        script_name: row.script_name || undefined,
        module_type: row.module_type || undefined,
        module: row.module || undefined,
        creative_format: row.creative_format || undefined,
        visual_format: row.visual_format || undefined,
        human_ai: row.human_ai || undefined,
        script_type: row.script_type || undefined,
        script: row.script || undefined,
        reference_link: row.reference_link || undefined,
        priority: row.priority || "low",
        comments: row.comments || undefined,
        due_delivery_date: dueDate,
        hooks_count:
          (row.hooks_count ?? "").toString().trim() !== ""
            ? Number(row.hooks_count)
            : undefined,
        auto_launch: handleParseAutoLaunch({ value: row.auto_launch }),
      };
    });

    setIsSubmitting(true);
    if (direct) {
      // All rows or none: the backend saves nothing if any row fails and returns that row's error.
      try {
        const items = payloads.map(({ requested_for_agency_id, requested_via, due_delivery_date, priority, hooks_count, ...brief }, i) => ({
          ...brief,
          ...directDeliveryFields(nonEmpty[i]),
        }));
        const result = await AdsAPIs.createDirectAdRequests({
          axiosPrivate,
          payload: { ...(agencyScoped ? {} : { agency_id: effectiveAgencyId }), items },
        });
        queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
        toast.success(`${result?.count ?? items.length} request(s) created, ${result?.delivery_count ?? 0} hook(s) sent to moderation.`);
        handleClose();
      } catch (err) {
        toast.error(directErrorMessage(err, nonEmpty.map(rowNo)));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    try {
      const result = await AdsAPIs.bulkCreateAdRequests({ axiosPrivate, requests: payloads });
      const created = result?.count ?? payloads.length;
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`${created} ad request(s) created.`);
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.error_message || err?.message || "Submit failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* ── Step 1: Agency selection (non-scoped users only) ── */}
      {step === "agency" && (
        <CustomModal open title={title} onClose={handleClose} isButtonsRequired={false}>
          <div className="space-y-4 py-2">
            {!agencyScoped ? (
              <div className="space-y-2">
                <Label>
                  Agency <span className="text-red-500">*</span>
                </Label>
                <Select value={agencyId} onValueChange={setAgencyId} disabled={agenciesLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                  <SelectContent>
                    {(agencies ?? []).map((a, i) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {agencyDisplayName(a, i)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Your account is not linked to an agency. Contact an administrator.
              </p>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleNext} disabled={!effectiveAgencyId}>
                Next
              </Button>
            </div>
          </div>
        </CustomModal>
      )}

      {/* ── Step 2: Open / import sheet ── */}
      {step === "sheets" && (
        <CustomModal open title={title} onClose={handleClose} isButtonsRequired={false}>
          <div className="flex flex-col items-center gap-5 py-8">
            <ul className="text-sm text-gray-600 text-left max-w-sm mx-auto space-y-1.5 list-disc list-inside">
              <li>A template sheet will open in a new tab with all required columns.</li>
              {direct ? (
                <>
                  <li>One row per request you are delivering now.</li>
                  <li>Fill <strong>hook_1_link</strong>, <strong>hook_2_link</strong>… with one Drive link per hook, without gaps.</li>
                  <li>QC is passed on submit and the hooks go straight to moderation.</li>
                </>
              ) : (
                <>
                  <li>Fill in <strong>App</strong> and <strong>Topic</strong> for each row (required).</li>
                  <li>Use <strong>DD-MM-YYYY</strong> format for due dates.</li>
                </>
              )}
              <li>Import here when done, review, then submit.</li>
            </ul>
            <Button type="button" onClick={createSheet} disabled={isCreatingSheet}>
              {isCreatingSheet ? "Creating Sheet…" : "Open in Google Sheets"}
            </Button>
            {sheetId && (
              <Button type="button" variant="outline" onClick={importFromSheet} disabled={isImportingSheet}>
                {isImportingSheet ? "Importing…" : "Import from Sheet"}
              </Button>
            )}
          </div>
          <div className="border-t border-gray-200 pt-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={agencyScoped ? handleClose : () => setStep("agency")}
            >
              {agencyScoped ? "Cancel" : "Back"}
            </Button>
          </div>
        </CustomModal>
      )}

      {/* ── Step 3: Review table ── */}
      {step === "table" && (
        <CustomModal
          open
          title={`${rows.length} rows imported — ${selectedRowIndices.length} selected`}
          onClose={handleClose}
          isButtonsRequired={false}
          className="!w-[95%] !max-w-7xl md:!w-[92%] lg:!w-[90%]"
        >
          <div className="max-h-[65vh] overflow-auto pb-3" ref={tableRef} onPaste={handleTablePaste}>
            <table className="w-full text-left text-sm border-collapse border border-gray-200">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="py-2 px-2 w-10 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedRowIndices.length === rows.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  {labels.map((label, idx) => (
                    <th
                      key={label}
                      className={`py-2 px-2 font-medium text-gray-600 whitespace-nowrap border border-gray-200 ${columnWidth(keys[idx])}`}
                    >
                      {label.endsWith("*") ? (
                        <>{label.slice(0, -1).trim()} <span className="text-red-500">*</span></>
                      ) : label}
                    </th>
                  ))}
                  <th className={`py-2 px-2 font-medium text-gray-600 whitespace-nowrap border border-gray-200 ${STICKY_ADSET_HEAD}`}>
                    Ad set name (auto)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="py-1 px-2 w-10 border border-gray-200">
                      <input
                        type="checkbox"
                        checked={selectedRowIndices.includes(i)}
                        onChange={() => toggleRow(i)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    {keys.map((key) => (
                      <td key={key} className={`py-0 px-0 border border-gray-200 ${columnWidth(key)}`}>
                        {key === "for_app_name" ? (
                          <SearchableSelect
                            value={row.for_app_name ?? ""}
                            options={cfg.appNames.map((a) => a.value)}
                            onChange={(v) => updateCell(i, "for_app_name", v)}
                          />
                        ) : key === "language" ? (
                          <SearchableSelect
                            value={row.language ?? ""}
                            options={cfg.languagesFor(row.for_app_name).map((l) => l.value)}
                            onChange={(v) => updateCell(i, "language", v)}
                          />
                        ) : key === "module_type" ? (
                          <SearchableSelect
                            value={row.module_type ?? ""}
                            options={cfg.moduleTypes.map((m) => m.value)}
                            onChange={(v) => updateCell(i, "module_type", v)}
                          />
                        ) : key === "human_ai" ? (
                          <SearchableSelect
                            value={row.human_ai ?? ""}
                            options={cfg.humanAi.map((o) => o.value)}
                            onChange={(v) => updateCell(i, "human_ai", v)}
                          />
                        ) : key === "script_type" ? (
                          <SearchableSelect
                            value={row.script_type ?? ""}
                            options={cfg.scriptTypes.map((o) => o.value)}
                            onChange={(v) => updateCell(i, "script_type", v)}
                          />
                        ) : key === "creative_format" ? (
                          <TwoLevelCell value={row.creative_format ?? ""} bases={cfg.creativeFormats} variationsFor={cfg.variationsFor} placeholder="Pick creative format" onChange={(v) => updateCell(i, "creative_format", v)} />
                        ) : key === "visual_format" ? (
                          <TwoLevelCell value={row.visual_format ?? ""} bases={cfg.visualFormats} variationsFor={cfg.visualVariationsFor} placeholder="Pick visual format" onChange={(v) => updateCell(i, "visual_format", v)} />
                        ) : key === "module" ? (
                          <MultiSelectCell
                            values={String(row.module ?? "").split("x").filter(Boolean)}
                            options={cfg.modulesFor(row.module_type)}
                            disabled={!row.module_type}
                            placeholder={row.module_type ? "Pick modules" : "Pick module type first"}
                            onChange={(vals) => updateCell(i, "module", composeModule(vals, cfg.modulesFor(row.module_type).map((m) => m.value)))}
                          />
                        ) : key === "auto_launch" ? (
                          <SearchableSelect
                            value={row.auto_launch ?? ""}
                            options={AUTO_LAUNCH_OPTIONS}
                            onChange={(v) => updateCell(i, "auto_launch", v)}
                          />
                        ) : key === "priority" ? (
                          <SearchableSelect
                            value={row.priority ?? ""}
                            options={PRIORITY_OPTIONS}
                            onChange={(v) => updateCell(i, "priority", v)}
                          />
                        ) : key === "category" ? (
                          <SearchableSelect
                            value={row.category ?? ""}
                            options={getCategories(row.for_app_name, row.language)}
                            onChange={(v) => updateCell(i, "category", v)}
                          />
                        ) : key === "track" ? (
                          <SearchableSelect
                            value={row.track ?? ""}
                            options={getTracks(row.for_app_name, row.language, row.category).map((t) => t.track)}
                            onChange={(v) => updateCell(i, "track", v)}
                          />
                        ) : key === "track_number" ? (
                          <input
                            value={row.track_number ?? ""}
                            readOnly
                            className="w-full min-w-0 bg-gray-50 py-1.5 px-2 text-sm text-gray-500"
                          />
                        ) : key === "ad_type" ? (
                          <SearchableSelect
                            value={row.ad_type ?? ""}
                            options={adTypeNames}
                            onChange={(v) => updateCell(i, "ad_type", v)}
                          />
                        ) : HOOK_LINK_KEY_SET.has(key) ? (
                          <input
                            disabled={direct && isUnusedHookCell(row, key)}
                            value={row[key] ?? ""}
                            onChange={(e) => updateCell(i, key, e.target.value)}
                            placeholder="https://drive.google.com/file/d/…"
                            className="w-full min-w-[240px] bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-100"
                          />
                        ) : key === "hooks_count" ? (
                          <SearchableSelect
                            value={row.hooks_count ?? ""}
                            options={HOOKS_COUNT_OPTIONS}
                            onChange={(v) => updateCell(i, "hooks_count", v)}
                          />
                        ) : (
                          <input
                            value={row[key] ?? ""}
                            onChange={(e) => updateCell(i, key, e.target.value)}
                            className="w-full min-w-0 bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
                          />
                        )}
                      </td>
                    ))}
                    <td className={`py-0 px-0 border border-gray-200 align-top ${STICKY_ADSET_CELL}`}>
                      <div className="whitespace-normal break-all px-2 py-1.5 font-mono text-[11px] leading-4 text-gray-600">
                        {handleGridAdsetName({ row, agencyName, direct })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-200 pt-3 flex items-center justify-between mt-2">
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              + Add row
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("sheets")}>
                Back
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || selectedRowIndices.length === 0}
              >
                {isSubmitting ? "Creating…" : `${direct ? "Create & deliver" : "Create"} (${selectedRowIndices.length})`}
              </Button>
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default AdRequestSheetModal;
